import React, { useState } from 'react';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { BaseHttpService } from 'cms/core/services';
import { Utils } from 'core/utils';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { useSnackbar } from 'notistack';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';

// World Map URL - using a topojson world map (will match by name if ISO codes not available)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Country name to ISO code mapping (fallback when map data doesn't include codes)
const countryNameToCode = {
  'algeria': 'DZ',
  'united states': 'US',
  'united kingdom': 'GB',
  'france': 'FR',
  'germany': 'DE',
  'italy': 'IT',
  'spain': 'ES',
  'canada': 'CA',
  'australia': 'AU',
  'japan': 'JP',
  'china': 'CN',
  'india': 'IN',
  'brazil': 'BR',
  'russia': 'RU',
  'south korea': 'KR',
  'mexico': 'MX',
  'netherlands': 'NL',
  'belgium': 'BE',
  'switzerland': 'CH',
  'sweden': 'SE',
  'norway': 'NO',
  'denmark': 'DK',
  'poland': 'PL',
  'portugal': 'PT',
  'greece': 'GR',
  'turkey': 'TR',
  'saudi arabia': 'SA',
  'egypt': 'EG',
  'south africa': 'ZA',
  'nigeria': 'NG',
  'kenya': 'KE',
  'argentina': 'AR',
  'chile': 'CL',
  'colombia': 'CO',
  'peru': 'PE',
  'venezuela': 'VE',
  'thailand': 'TH',
  'vietnam': 'VN',
  'indonesia': 'ID',
  'malaysia': 'MY',
  'singapore': 'SG',
  'philippines': 'PH',
  'new zealand': 'NZ',
  'ireland': 'IE',
  'finland': 'FI',
  'austria': 'AT',
  'czech republic': 'CZ',
  'hungary': 'HU',
  'romania': 'RO',
  'ukraine': 'UA',
  'israel': 'IL',
  'united arab emirates': 'AE',
  'qatar': 'QA',
  'kuwait': 'KW',
  'pakistan': 'PK',
  'bangladesh': 'BD',
  'sri lanka': 'LK',
  'myanmar': 'MM',
  'cambodia': 'KH',
  'laos': 'LA',
  'mongolia': 'MN',
  'north korea': 'KP',
  'taiwan': 'TW',
  'hong kong': 'HK',
  'morocco': 'MA',
  'tunisia': 'TN',
  'libya': 'LY',
  'sudan': 'SD',
  'ethiopia': 'ET',
  'tanzania': 'TZ',
  'uganda': 'UG',
  'ghana': 'GH',
  'ivory coast': 'CI',
  'senegal': 'SN',
  'cameroon': 'CM',
  'angola': 'AO',
  'mozambique': 'MZ',
  'madagascar': 'MG',
  'zimbabwe': 'ZW',
  'zambia': 'ZM',
  'botswana': 'BW',
  'namibia': 'NA',
  'chile': 'CL',
  'ecuador': 'EC',
  'bolivia': 'BO',
  'paraguay': 'PY',
  'uruguay': 'UY',
  'cuba': 'CU',
  'jamaica': 'JM',
  'haiti': 'HT',
  'dominican republic': 'DO',
  'guatemala': 'GT',
  'honduras': 'HN',
  'el salvador': 'SV',
  'nicaragua': 'NI',
  'costa rica': 'CR',
  'panama': 'PA',
  'belize': 'BZ',
  'iceland': 'IS',
  'luxembourg': 'LU',
  'slovakia': 'SK',
  'slovenia': 'SI',
  'croatia': 'HR',
  'serbia': 'RS',
  'bulgaria': 'BG',
  'albania': 'AL',
  'macedonia': 'MK',
  'bosnia and herzegovina': 'BA',
  'montenegro': 'ME',
  'kosovo': 'XK',
  'moldova': 'MD',
  'belarus': 'BY',
  'lithuania': 'LT',
  'latvia': 'LV',
  'estonia': 'EE',
  'georgia': 'GE',
  'armenia': 'AM',
  'azerbaijan': 'AZ',
  'kazakhstan': 'KZ',
  'uzbekistan': 'UZ',
  'turkmenistan': 'TM',
  'kyrgyzstan': 'KG',
  'tajikistan': 'TJ',
  'afghanistan': 'AF',
  'iraq': 'IQ',
  'iran': 'IR',
  'syria': 'SY',
  'lebanon': 'LB',
  'jordan': 'JO',
  'yemen': 'YE',
  'oman': 'OM',
  'bahrain': 'BH'
};

// World Map Component with real interactive map
const WorldMap = ({ selectedCountry, countries = [], onCountryClick, mapFilterCountry, onCountryClickWithName }) => {
  const theme = useTheme();
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredCountryName, setHoveredCountryName] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Create maps for country lookup by code and name
  const countryCodeMap = React.useMemo(() => {
    const codeMap = {};
    const nameMap = {};
    countries.forEach(country => {
      if (country.code) {
        codeMap[country.code.toLowerCase()] = country;
      }
      // Also create a name map for fallback matching
      const countryName = Utils.getDefaultName(country);
      if (countryName) {
        nameMap[countryName.toLowerCase().trim()] = country;
      }
    });
    return { codeMap, nameMap };
  }, [countries]);
  
  // Helper function to find country from geo properties
  const findCountryFromGeo = (geo) => {
    // Try ISO_A2 code first
    const isoCode = geo.properties?.ISO_A2?.toLowerCase() || 
                    geo.properties?.ISO_A3?.toLowerCase() ||
                    geo.properties?.iso_a2?.toLowerCase() ||
                    geo.properties?.iso_a3?.toLowerCase();
    
    if (isoCode && countryCodeMap.codeMap[isoCode]) {
      return countryCodeMap.codeMap[isoCode];
    }
    
    // Try by country name as fallback
    const geoName = geo.properties?.NAME || 
                    geo.properties?.name || 
                    geo.properties?.NAME_LONG ||
                    geo.properties?.NAME_EN;
    
    if (geoName && countryCodeMap.nameMap[geoName.toLowerCase().trim()]) {
      return countryCodeMap.nameMap[geoName.toLowerCase().trim()];
    }
    
    return null;
  };

  const getCountryFill = (geo) => {
    const country = findCountryFromGeo(geo);
    
    if (mapFilterCountry && country && country.id === mapFilterCountry.id) {
      return '#1976d2'; // Selected/filtered country - dark blue
    }
    if (selectedCountry && country && country.id === selectedCountry.id) {
      return '#42a5f5'; // Currently selected country - light blue
    }
    if (country) {
      // Active countries in database - bright green/teal
      if (country.status !== false) {
        return theme.palette.mode === 'dark' ? '#10b981' : '#34d399'; // Active - bright green
      } else {
        return theme.palette.mode === 'dark' ? '#6b7280' : '#9ca3af'; // Inactive - gray
      }
    }
    // Countries not in database - very dim
    return theme.palette.mode === 'dark' ? '#1f2937' : '#f3f4f6';
  };

  const getCountryStroke = (geo) => {
    const country = findCountryFromGeo(geo);
    
    if (mapFilterCountry && country && country.id === mapFilterCountry.id) {
      return '#1565c0'; // Dark blue stroke for filtered
    }
    if (selectedCountry && country && country.id === selectedCountry.id) {
      return '#1976d2'; // Blue stroke for selected
    }
    if (country) {
      if (country.status !== false) {
        return theme.palette.mode === 'dark' ? '#059669' : '#10b981'; // Green stroke for active
      } else {
        return theme.palette.mode === 'dark' ? '#4b5563' : '#6b7280'; // Gray stroke for inactive
      }
    }
    return theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb'; // Very light stroke for countries not in DB
  };

  const getCountryNameFromGeo = (geo) => {
    return geo.properties?.NAME || 
           geo.properties?.name || 
           geo.properties?.NAME_LONG ||
           geo.properties?.NAME_EN ||
           'Unknown Country';
  };

  const handleCountryClick = (geo, event) => {
    const country = findCountryFromGeo(geo);
    const countryName = getCountryNameFromGeo(geo);
    
    if (onCountryClickWithName) {
      onCountryClickWithName(country, countryName, geo);
    } else if (country && onCountryClick) {
      onCountryClick(country);
    }
  };

  const handleMouseMove = (event) => {
    if (hoveredCountry) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({ 
        x: event.clientX - rect.left, 
        y: event.clientY - rect.top 
      });
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        bgcolor: 'background.default',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box
        onMouseMove={handleMouseMove}
        sx={{ width: '100%', height: '100%', position: 'relative' }}
      >
        <ComposableMap
          projectionConfig={{
            scale: 200,
          }}
          style={{ width: '100%', height: '100%' }}
        >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const country = findCountryFromGeo(geo);
                const fillColor = getCountryFill(geo);
                const strokeColor = getCountryStroke(geo);
                const countryName = getCountryNameFromGeo(geo);
                const isHovered = hoveredCountry === geo.rsmKey;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isHovered ? (country ? (country.status !== false ? '#22d3ee' : '#64b5f6') : '#94a3b8') : fillColor}
                    stroke={isHovered ? '#1976d2' : strokeColor}
                    strokeWidth={isHovered ? 2.5 : 0.5}
                    style={{
                      default: {
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      },
                      hover: {
                        fill: country ? (country.status !== false ? '#22d3ee' : '#64b5f6') : '#94a3b8',
                        stroke: '#1976d2',
                        strokeWidth: 2.5,
                        outline: 'none',
                        opacity: 0.95
                      },
                      pressed: {
                        fill: country ? '#42a5f5' : '#64748b',
                        outline: 'none'
                      }
                    }}
                    onMouseEnter={(e) => {
                      setHoveredCountry(geo.rsmKey);
                      setHoveredCountryName(getCountryNameFromGeo(geo));
                      const rect = e.currentTarget.closest('svg')?.getBoundingClientRect();
                      if (rect) {
                        setTooltipPosition({ 
                          x: e.clientX - rect.left, 
                          y: e.clientY - rect.top 
                        });
                      }
                    }}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.closest('svg')?.getBoundingClientRect();
                      if (rect) {
                        setTooltipPosition({ 
                          x: e.clientX - rect.left, 
                          y: e.clientY - rect.top 
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredCountry(null);
                      setHoveredCountryName(null);
                    }}
                    onClick={(event) => handleCountryClick(geo, event)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          bgcolor: 'background.paper',
          p: 1,
          borderRadius: 1.5,
          boxShadow: 2,
          border: '1px solid',
          borderColor: 'divider',
          zIndex: 10
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PublicIcon color="primary" fontSize="small" sx={{ fontSize: '0.875rem' }} />
          <Typography variant="caption" fontWeight={600} fontSize="0.7rem">
            World Map
          </Typography>
        </Stack>
      </Box>
      {/* Tooltip for country name on hover */}
      {hoveredCountry && hoveredCountryName && (
        <Box
          sx={{
            position: 'absolute',
            left: tooltipPosition.x + 15,
            top: tooltipPosition.y - 35,
            bgcolor: 'background.paper',
            color: 'text.primary',
            p: 0.75,
            px: 1.5,
            borderRadius: 1,
            boxShadow: 4,
            border: '1px solid',
            borderColor: 'divider',
            zIndex: 1000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          <Typography variant="caption" fontWeight={600} fontSize="0.75rem">
            {hoveredCountryName}
          </Typography>
        </Box>
      )}
      {mapFilterCountry && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            p: 1,
            borderRadius: 1.5,
            boxShadow: 2,
            zIndex: 10
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" fontWeight={600} fontSize="0.7rem">
              Filtered: {Utils.getDefaultName(mapFilterCountry) || mapFilterCountry.code}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onCountryClick && onCountryClick(null)}
              sx={{ 
                color: 'inherit',
                p: 0.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>✕</Typography>
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

function CountriesManagementPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [mapFilterCountry, setMapFilterCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [countryFormData, setCountryFormData] = useState({ code: '', name: '', status: true });
  const [cityFormData, setCityFormData] = useState({ name: '', status: true });
  const [defaultLanguageId, setDefaultLanguageId] = useState(1);

  // Load default language
  const { data: languagesData } = useQuery(
    'admin_languages',
    async () => {
      return await BaseHttpService.loadLanguages();
    },
    {
      onSuccess: (data) => {
        const languages = data?.results || data || [];
        const defaultLang = languages.find(lang => lang.is_default) || languages[0];
        if (defaultLang) {
          setDefaultLanguageId(defaultLang.id);
        }
      }
    }
  );

  const { data: countriesData, isLoading: countriesLoading } = useQuery(
    'admin_countries',
    async () => {
      const service = new HttpService();
      return await service.get('/base/country/?size=10000', null);
    }
  );

  const { data: citiesData, isLoading: citiesLoading } = useQuery(
    ['admin_cities', selectedCountry?.id],
    async () => {
      if (!selectedCountry?.id) return { results: [] };
      return await BaseHttpService.loadCities(selectedCountry.id);
    },
    { enabled: !!selectedCountry?.id }
  );

  const countries = countriesData?.results || countriesData || [];
  const cities = citiesData?.results || citiesData || [];

  const filteredCountries = React.useMemo(() => {
    let filtered = countries;
    
    // Filter by map selection first
    if (mapFilterCountry) {
      filtered = filtered.filter(country => country.id === mapFilterCountry.id);
    }
    
    // Then filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(country => {
        const countryName = Utils.getDefaultName(country);
        const code = country.code?.toLowerCase() || '';
        return countryName?.toLowerCase().includes(term) || code.includes(term);
      });
    }
    
    return filtered;
  }, [countries, searchTerm, mapFilterCountry]);
  
  const handleMapCountryClick = (country, countryName, geo) => {
    if (country) {
      // Country exists - select it to show cities
      setMapFilterCountry(country);
      setSelectedCountry(country);
      // Clear search to show the filtered country
      setSearchTerm('');
    } else if (countryName) {
      // Country doesn't exist - open dialog to add it with prefilled name
      // Try to extract country code from geo properties first
      let isoCode = geo?.properties?.ISO_A2 || 
                    geo?.properties?.ISO_A3 ||
                    geo?.properties?.iso_a2 ||
                    geo?.properties?.iso_a3 ||
                    geo?.properties?.ISO ||
                    geo?.properties?.iso ||
                    '';
      
      // If no code found in properties, try mapping from country name
      if (!isoCode && countryName) {
        const normalizedName = countryName.toLowerCase().trim();
        isoCode = countryNameToCode[normalizedName] || '';
      }
      
      setCountryFormData({ 
        code: isoCode ? isoCode.toUpperCase() : '', 
        name: countryName, 
        status: true 
      });
      setEditingCountry(null);
      setCountryDialogOpen(true);
    } else {
      setMapFilterCountry(null);
    }
  };

  const filteredCities = React.useMemo(() => {
    if (!citySearchTerm) return cities;
    const term = citySearchTerm.toLowerCase();
    return cities.filter(city => {
      const cityName = Utils.getDefaultName(city);
      return cityName?.toLowerCase().includes(term);
    });
  }, [cities, citySearchTerm]);

  const countryMutation = useMutation(
    async (data) => {
      const service = new HttpService();
      // Prepare country data with names array
      const countryData = {
        code: data.code,
        status: data.status,
        names: data.name ? [{ name: data.name, language: defaultLanguageId }] : []
      };
      if (editingCountry) {
        // For update, include existing names and update/add the default language name
        const existingNames = editingCountry.names || [];
        const nameIndex = existingNames.findIndex(n => n.language === defaultLanguageId);
        if (nameIndex >= 0 && data.name) {
          existingNames[nameIndex].name = data.name;
        } else if (data.name) {
          existingNames.push({ name: data.name, language: defaultLanguageId });
        }
        countryData.names = existingNames;
        return await service.put(`/base/country/${editingCountry.id}/`, countryData);
      }
      return await service.post('/base/country/', countryData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin_countries');
        enqueueSnackbar(`Country ${editingCountry ? 'updated' : 'created'} successfully`, { variant: 'success' });
        setCountryDialogOpen(false);
        setEditingCountry(null);
        setCountryFormData({ code: '', name: '', status: true });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to save country', { variant: 'error' });
      }
    }
  );

  const cityMutation = useMutation(
    async (data) => {
      const service = new HttpService();
      // Prepare city data - since CitySerializer doesn't handle names in the current implementation,
      // we'll send status and let the backend handle name creation separately if needed
      const cityData = {
        status: data.status
      };
      // Try to include names if the backend supports it
      if (data.name) {
        cityData.names = [{ name: data.name, language: defaultLanguageId }];
      }
      if (editingCity) {
        // For update, try to preserve existing names structure
        if (editingCity.names && Array.isArray(editingCity.names)) {
          const existingNames = [...editingCity.names];
          const nameIndex = existingNames.findIndex(n => n.language === defaultLanguageId);
          if (nameIndex >= 0 && data.name) {
            existingNames[nameIndex].name = data.name;
          } else if (data.name) {
            existingNames.push({ name: data.name, language: defaultLanguageId });
          }
          cityData.names = existingNames;
        } else if (data.name) {
          cityData.names = [{ name: data.name, language: defaultLanguageId }];
        }
        return await service.put(`/base/city/${editingCity.id}/`, cityData);
      }
      return await service.post(`/base/country/${selectedCountry.id}/cities/`, cityData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin_cities', selectedCountry?.id]);
        enqueueSnackbar(`City ${editingCity ? 'updated' : 'created'} successfully`, { variant: 'success' });
        setCityDialogOpen(false);
        setEditingCity(null);
        setCityFormData({ name: '', status: true });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to save city', { variant: 'error' });
      }
    }
  );

  const deleteCountryMutation = useMutation(
    async (id) => {
      const service = new HttpService();
      return await service.delete(`/base/country/${id}/`, null);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin_countries');
        enqueueSnackbar('Country deleted successfully', { variant: 'success' });
        if (selectedCountry?.id === editingCountry?.id) {
          setSelectedCountry(null);
        }
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete country', { variant: 'error' });
      }
    }
  );

  const deleteCityMutation = useMutation(
    async (id) => {
      const service = new HttpService();
      return await service.delete(`/base/city/${id}/`, null);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin_cities', selectedCountry?.id]);
        enqueueSnackbar('City deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete city', { variant: 'error' });
      }
    }
  );

  const handleOpenCountryDialog = (country = null) => {
    setEditingCountry(country);
    if (country) {
      const countryName = Utils.getDefaultName(country);
      setCountryFormData({ 
        code: country.code || '', 
        name: countryName || '',
        status: country.status !== false 
      });
    } else {
      setCountryFormData({ code: '', name: '', status: true });
    }
    setCountryDialogOpen(true);
  };

  const handleOpenCityDialog = (city = null) => {
    if (!selectedCountry) {
      enqueueSnackbar('Please select a country first', { variant: 'warning' });
      return;
    }
    setEditingCity(city);
    if (city) {
      setCityFormData({ name: Utils.getDefaultName(city) || '', status: city.status !== false });
    } else {
      setCityFormData({ name: '', status: true });
    }
    setCityDialogOpen(true);
  };

  const handleSaveCountry = () => {
    countryMutation.mutate(countryFormData);
  };

  const handleSaveCity = () => {
    cityMutation.mutate(cityFormData);
  };

  return (
    <AdminLayout pageTitle="Countries & Cities">
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)' }}>
        {/* Left Side - Countries */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Countries
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenCountryDialog()}
                >
                  Add Country
                </Button>
              </Stack>
              <TextField
                fullWidth
                size="small"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {countriesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredCountries.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'No countries found' : 'No countries available'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {filteredCountries.map((country, index) => {
                    const countryName = Utils.getDefaultName(country);
                    const isSelected = selectedCountry?.id === country.id;
                    return (
                      <React.Fragment key={country.id}>
                        <ListItem
                          button
                          selected={isSelected}
                          onClick={() => setSelectedCountry(country)}
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'primary.light',
                              '&:hover': {
                                bgcolor: 'primary.light'
                              }
                            }
                          }}
                        >
                          <ListItemText
                            primary={countryName || country.code}
                            secondary={country.code}
                          />
                          <ListItemSecondaryAction>
                            <Stack direction="row" spacing={1}>
                              <Chip
                                label={country.status ? 'Active' : 'Inactive'}
                                size="small"
                                color={country.status ? 'success' : 'default'}
                                variant="outlined"
                              />
                              <Tooltip title="Edit">
                                <IconButton color="primary"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenCountryDialog(country);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Are you sure you want to delete this country?')) {
                                      deleteCountryMutation.mutate(country.id);
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < filteredCountries.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Cities & Map */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3} sx={{ height: '100%' }}>
            {/* Cities List */}
            <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    Cities
                    {selectedCountry && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({Utils.getDefaultName(selectedCountry)})
                      </Typography>
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCityDialog()}
                    disabled={!selectedCountry}
                  >
                    Add City
                  </Button>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search cities..."
                  value={citySearchTerm}
                  onChange={(e) => setCitySearchTerm(e.target.value)}
                  sx={{ mt: 2 }}
                  disabled={!selectedCountry}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {!selectedCountry ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <LocationCityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Select a country to view cities
                    </Typography>
                  </Box>
                ) : citiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredCities.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {citySearchTerm ? 'No cities found' : 'No cities available for this country'}
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {filteredCities.map((city, index) => {
                      const cityName = Utils.getDefaultName(city);
                      return (
                        <React.Fragment key={city.id}>
                          <ListItem>
                            <ListItemText
                              primary={cityName || `City ${city.id}`}
                              secondary={`ID: ${city.id}`}
                            />
                            <ListItemSecondaryAction>
                              <Stack direction="row" spacing={1}>
                                <Chip
                                  label={city.status ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={city.status ? 'success' : 'default'}
                                  variant="outlined"
                                />
                                <Tooltip title="Edit">
                                  <IconButton color="primary"
                                    size="small"
                                    onClick={() => handleOpenCityDialog(city)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      if (window.confirm('Are you sure you want to delete this city?')) {
                                        deleteCityMutation.mutate(city.id);
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < filteredCities.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Paper>

            {/* World Map */}
            <Paper sx={{ height: 250, overflow: 'hidden' }}>
              <WorldMap 
                selectedCountry={selectedCountry} 
                countries={countries}
                mapFilterCountry={mapFilterCountry}
                onCountryClick={handleMapCountryClick}
                onCountryClickWithName={handleMapCountryClick}
              />
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Country Dialog */}
      <Dialog open={countryDialogOpen} onClose={() => setCountryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCountry ? 'Edit Country' : 'Add Country'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Country Code"
              value={countryFormData.code}
              onChange={(e) => setCountryFormData({ ...countryFormData, code: e.target.value })}
              placeholder="e.g., US, GB, FR"
              required
            />
            <TextField
              fullWidth
              label="Country Name"
              value={countryFormData.name}
              onChange={(e) => setCountryFormData({ ...countryFormData, name: e.target.value })}
              placeholder="e.g., United States, United Kingdom"
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={countryFormData.status}
                  onChange={(e) => setCountryFormData({ ...countryFormData, status: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCountry}
            disabled={!countryFormData.code || !countryFormData.name || countryMutation.isLoading}
          >
            {countryMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* City Dialog */}
      <Dialog open={cityDialogOpen} onClose={() => setCityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCity ? 'Edit City' : 'Add City'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="City Name"
              value={cityFormData.name}
              onChange={(e) => setCityFormData({ ...cityFormData, name: e.target.value })}
              placeholder="Enter city name"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={cityFormData.status}
                  onChange={(e) => setCityFormData({ ...cityFormData, status: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCity}
            disabled={!cityFormData.name || cityMutation.isLoading}
          >
            {cityMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}

export default CountriesManagementPage;

